import React from 'react';
import { ShieldCheck } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { Input } from '@cotera/client/v0/components/ui/input';
import { WebhookConfigCard } from './webhook-config-card';

type VerificationAlgorithm =
  | 'cotera'
  | 'sha256_hex_prefixed'
  | 'hex_v0_prefixed'
  | 'hmac_sha256_base64';

type SignedPayloadPart = 'body' | 'timestamp' | 'method' | 'uri';

type CustomVerificationConfig = {
  signatureHeaders?: string[];
  verification?: VerificationAlgorithm;
  timestampHeader?: string | null;
  signedPayload?: SignedPayloadPart[];
  maxTimestampSkewMs?: number;
};

type GenericVerificationConfig = {
  profile: 'cotera' | 'custom';
  config?: CustomVerificationConfig;
};

const DEFAULT_SIGNATURE_HEADER = 'x-cotera-signature-256';
const DEFAULT_SIGNED_PAYLOAD: SignedPayloadPart[] = ['body'];

function toGenericVerificationConfig(
  value: unknown
): GenericVerificationConfig {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { profile: 'cotera' };
  }
  const record = value as Record<string, unknown>;
  if (record['profile'] === 'custom') {
    return {
      profile: 'custom',
      config: toCustomVerificationConfig(record['config']),
    };
  }
  return { profile: 'cotera' };
}

function toCustomVerificationConfig(value: unknown): CustomVerificationConfig {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {};
  }
  return value as CustomVerificationConfig;
}

function makeCustomConfig(
  config: CustomVerificationConfig | undefined,
  patch: Partial<CustomVerificationConfig>
): GenericVerificationConfig {
  return {
    profile: 'custom',
    config: {
      signatureHeaders: config?.signatureHeaders ?? [DEFAULT_SIGNATURE_HEADER],
      verification: config?.verification ?? 'cotera',
      signedPayload: config?.signedPayload ?? DEFAULT_SIGNED_PAYLOAD,
      ...patch,
    },
  };
}

export const WebhookVerificationSection: React.FC<{
  verificationConfig: unknown;
  verificationConfigSchema?: Record<string, unknown>;
  onChange: (next: unknown) => void;
}> = ({ verificationConfig, onChange }) => {
  const parsed = toGenericVerificationConfig(verificationConfig);
  const customConfig = parsed.config;
  const signatureHeader =
    customConfig?.signatureHeaders?.[0] ?? DEFAULT_SIGNATURE_HEADER;
  const verification = customConfig?.verification ?? 'cotera';
  const timestampHeader = customConfig?.timestampHeader ?? '';
  const signedPayload = (
    customConfig?.signedPayload ?? DEFAULT_SIGNED_PAYLOAD
  ).join(', ');
  const maxTimestampSkewMs = customConfig?.maxTimestampSkewMs;

  return (
    <div className="space-y-2">
      <Text.Caption className="text-muted-foreground block">
        Verification
      </Text.Caption>
      <Select
        value={parsed.profile}
        onValueChange={(next) => {
          if (next === 'custom') {
            onChange(makeCustomConfig(customConfig, {}));
            return;
          }
          onChange({ profile: 'cotera' });
        }}
      >
        <SelectTrigger className="h-9 w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="cotera">
            Cotera token auth (recommended)
          </SelectItem>
          <SelectItem value="custom">Custom signature</SelectItem>
        </SelectContent>
      </Select>
      {parsed.profile === 'cotera' ? (
        <Text.Caption className="text-muted-foreground block text-xs">
          Send the webhook secret as Authorization: Bearer &lt;secret&gt;,
          x-cotera-webhook-token, cotera_webhook_token, or auth_token.
        </Text.Caption>
      ) : (
        <WebhookConfigCard
          icon={<ShieldCheck />}
          title="Signature verification"
          description="Configure how Cotera verifies the request signature. The webhook secret shown below is the HMAC key."
        >
          <div className="space-y-1.5">
            <Text.Caption className="text-muted-foreground block">
              Signature header
            </Text.Caption>
            <Input
              value={signatureHeader}
              onChange={(event) =>
                onChange(
                  makeCustomConfig(customConfig, {
                    signatureHeaders: [event.target.value],
                  })
                )
              }
              placeholder={DEFAULT_SIGNATURE_HEADER}
            />
          </div>
          <div className="space-y-1.5">
            <Text.Caption className="text-muted-foreground block">
              Signature format
            </Text.Caption>
            <Select
              value={verification}
              onValueChange={(next) =>
                onChange(
                  makeCustomConfig(customConfig, {
                    verification: next as VerificationAlgorithm,
                  })
                )
              }
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cotera">
                  sha256=&lt;hex&gt; over body
                </SelectItem>
                <SelectItem value="sha256_hex_prefixed">
                  sha256=&lt;hex&gt; over body
                </SelectItem>
                <SelectItem value="hex_v0_prefixed">
                  v0=&lt;hex&gt; over v0:timestamp:body
                </SelectItem>
                <SelectItem value="hmac_sha256_base64">
                  base64 HMAC over selected payload
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {verification === 'hex_v0_prefixed' ||
          verification === 'hmac_sha256_base64' ? (
            <div className="space-y-1.5">
              <Text.Caption className="text-muted-foreground block">
                Timestamp header
              </Text.Caption>
              <Input
                value={timestampHeader}
                onChange={(event) => {
                  const timestampHeaderValue = event.target.value.trim();
                  onChange(
                    makeCustomConfig(customConfig, {
                      timestampHeader:
                        timestampHeaderValue === ''
                          ? null
                          : timestampHeaderValue,
                    })
                  );
                }}
                placeholder="x-webhook-timestamp"
              />
            </div>
          ) : null}
          {verification === 'hmac_sha256_base64' ? (
            <>
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Signed payload parts
                </Text.Caption>
                <Text.Caption className="text-muted-foreground block text-xs">
                  Comma-separated, in order. Supported: body, timestamp, method,
                  uri.
                </Text.Caption>
                <Input
                  value={signedPayload}
                  onChange={(event) =>
                    onChange(
                      makeCustomConfig(customConfig, {
                        signedPayload: parseSignedPayload(event.target.value),
                      })
                    )
                  }
                  placeholder="body"
                />
              </div>
              <div className="space-y-1.5">
                <Text.Caption className="text-muted-foreground block">
                  Max timestamp skew (ms)
                </Text.Caption>
                <Input
                  type="number"
                  value={maxTimestampSkewMs ?? ''}
                  onChange={(event) => {
                    const parsedNumber = Number(event.target.value);
                    onChange(
                      makeCustomConfig(customConfig, {
                        maxTimestampSkewMs:
                          Number.isFinite(parsedNumber) && parsedNumber > 0
                            ? parsedNumber
                            : undefined,
                      })
                    );
                  }}
                  placeholder="300000"
                />
              </div>
            </>
          ) : null}
        </WebhookConfigCard>
      )}
    </div>
  );
};

function parseSignedPayload(value: string): SignedPayloadPart[] {
  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter(
      (part): part is SignedPayloadPart =>
        part === 'body' ||
        part === 'timestamp' ||
        part === 'method' ||
        part === 'uri'
    );
  if (parts.length === 0) {
    return DEFAULT_SIGNED_PAYLOAD;
  }
  return parts;
}
